// ========================================================================
// 📁 JobDetail.js
// Purpose: Manages the state for a single job (modals, etc.) and uses JobSection.
// ========================================================================
import React, { useEffect, useState } from "react";
import { AlertTriangle, Camera, Palette } from "lucide-react";
import { JobSection } from "./JobSection";
import { ApprovalActions } from "./ApprovalActions";
import LatLong from "../Map/LatLong";
import ApproveBrandModel from "../Popup/ApproveBrandModel";
import RejectionReasonModal from "../Popup/RejectedReasonModel";
import { BRANDS } from "./constants";
import axios from "axios";
import toast, { Toaster } from "react-hot-toast";

export const JobDetail = ({
  job,
  index,
  share,
  taskData,
  onAction,
  updateReceeBrand,
}) => {
  const [isApprovalModalOpen, setApprovalModalOpen] = useState(false);
  const [isRejectionModalOpen, setRejectionModalOpen] = useState({
    before: false,
    after: false,
  });
  const [remark, setRemark] = useState("");
  const [brands, setBrands] = useState([]);

  const getBrands = async () => {
    const userToken = localStorage.getItem("user-token");
    const headers = { authorization: userToken };
    try {
      const response = await axios.get(`/brand`, { headers });
      setBrands(response.data.data);
    } catch (error) {
      console.error("Error fetching Brands:", error);
      toast.error("Error fetching Brands.");
    }
  };

  useEffect(() => {
    getBrands();
  }, []);

  const handleDeny = (isBefore) => {
    setRejectionModalOpen((prev) => ({
      ...prev,
      [isBefore ? "before" : "after"]: true,
    }));
  };

  const handleDenyConfirm = (isBefore, reason) => {
    onAction(job._id, isBefore, false, { rejectedReason: reason });
    setRejectionModalOpen({ before: false, after: false });
  };

  const handleApproveConfirm = (brand, remark) => {
    onAction(job._id, true, true, {
      approvedBrand: brand,
      rejectedReason: remark,
    });
    setApprovalModalOpen(false);
  };

  return (
    <div className="bg-white rounded-2xl border border-gray-200 shadow-md overflow-hidden">
      <div className="bg-gray-100 px-6 py-3 border-b">
        <h3 className="text-lg font-bold text-gray-800">
          Task #{index + 1}:{" "}
          <span className="font-normal">
            {job.taskType} {job.task && ` - ${job.task}`}
          </span>
        </h3>
      </div>
      <div className="p-4 grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* BEFORE SECTION */}
        <JobSection
          title="Before"
          jobId={job._id}
          data={job.before}
          icon={<Camera className="w-6 h-6 text-green-600" />}
          gradient="from-green-50 to-emerald-50"
          borderColor="border-green-200"
          actions={
            <ApprovalActions
              sentForApproval={taskData.sentForApprovalBefore}
              data={job.before}
              defaultBrand={job.approvedBrand || taskData.brand}
              onApprove={() => setApprovalModalOpen(true)}
              onDeny={() => handleDeny(true)}
              share={share}
              onBrandUpdate={(brand) => updateReceeBrand(job._id, brand)}
            />
          }
        />

        {/* DESIGN SECTION */}
        <JobSection
          title="Design"
          jobId={job._id}
          data={job.designImage ? { images: [job.designImage] } : null}
          icon={<Palette className="w-6 h-6 text-purple-600" />}
          gradient="from-purple-50 to-pink-50"
          borderColor="border-purple-200"
          actions={
            <div>
              <h3 className="text-lg font-normal text-gray-500 dark:text-gray-400">
                Brand:
              </h3>
              <select
                className=" select select-bordered w-full"
                value={job.approvedBrand || taskData.brand || ""}
                disabled={job.before.approved}
                onChange={(e) => updateReceeBrand(job._id, e.target.value)}
              >
                {brands.map((brandItem) => (
                  <option key={brandItem._id} value={brandItem.name}>
                    {brandItem.name}
                  </option>
                ))}
              </select>
            </div>
          }
        />

        {/* AFTER SECTION */}
        <JobSection
          title="After"
          jobId={job._id}
          data={job.after}
          icon={<Camera className="w-6 h-6 text-blue-600" />}
          gradient="from-blue-50 to-cyan-50"
          borderColor="border-blue-200"
          actions={
            <ApprovalActions
              sentForApproval={
                job.before.approved && taskData.sentForApprovalAfter
              }
              data={job.after}
              onApprove={() => onAction(job._id, false, true)}
              onDeny={() => handleDeny(false)}
              share={share}
            />
          }
        />
      </div>

      {/* MAP SECTION */}
      {job?.before?.latlong && (
        <div className="p-4">
          <LatLong
            markers={[job.before.latlong, job.after?.latlong].filter(Boolean)}
          />
        </div>
      )}

      {/* MODALS */}
      {isApprovalModalOpen && (
        <ApproveBrandModel
          isOpen={isApprovalModalOpen}
          onClose={() => setApprovalModalOpen(false)}
          onConfirm={handleApproveConfirm}
          brand={job.approvedBrand || taskData.brand}
          channelProgramme={taskData.channel_programme}
        />
      )}
      {isRejectionModalOpen.before && (
        <RejectionReasonModal
          isOpen={true}
          onClose={() =>
            setRejectionModalOpen((p) => ({ ...p, before: false }))
          }
          onConfirm={(reason) => handleDenyConfirm(true, reason)}
        />
      )}
      {isRejectionModalOpen.after && (
        <RejectionReasonModal
          isOpen={true}
          onClose={() => setRejectionModalOpen((p) => ({ ...p, after: false }))}
          onConfirm={(reason) => handleDenyConfirm(false, reason)}
        />
      )}
    </div>
  );
};
